import { el, on } from '../util/dom';
import { UsageAnalyticsState, DailyLimitPrediction } from './types';
import { loadUsageAnalytics } from './hooks';
import {
  createDailyUsagePieChart,
  createWeeklyUsageBarChart,
  createBlockEventsSummary,
  createUsageTrendsView,
  createDailyLimitPrediction,
} from './charts';
import { USAGE_DASHBOARD_STYLES } from './styles';

export interface UsageDashboardProps {
  familyId: string;
  deviceId: string;
  dailyLimit?: number;
  onError?: (error: string) => void;
}

export async function createUsageDashboard(props: UsageDashboardProps): Promise<HTMLElement> {
  const container = el('div', { class: 'usage-dashboard-container' });

  // Add styles
  injectUsageDashboardStyles();

  // Loading state
  const loading = el('div', { class: 'usage-loading' });
  loading.appendChild(el('div', { class: 'spinner' }, ''));
  loading.appendChild(el('div', { class: 'loading-text' }, 'Loading analytics...'));
  container.appendChild(loading);

  // Load analytics data
  try {
    const result = await loadUsageAnalytics(
      props.familyId,
      props.deviceId,
      props.dailyLimit || 120
    );

    // Remove loading state
    loading.remove();

    if (result.error) {
      const errorDiv = el('div', { class: 'usage-error' }, `Error: ${result.error}`);
      container.appendChild(errorDiv);
      props.onError?.(result.error);
      return container;
    }

    // Create content sections
    const content = el('div', { class: 'usage-content' });

    // Header
    const header = el('div', { class: 'usage-header' });
    const title = el('h2', { class: 'usage-title' }, 'Usage Analytics');
    const dateRange = el('div', { class: 'usage-date-range' }, 'This week');
    header.appendChild(title);
    header.appendChild(dateRange);
    content.appendChild(header);

    // Main grid
    const grid = el('div', { class: 'usage-grid' });

    // Row 1: Today's usage + Prediction
    if (result.today) {
      const row1 = el('div', { class: 'usage-row' });

      const pieSection = el('div', { class: 'usage-section pie-section' });
      pieSection.appendChild(createDailyUsagePieChart(result.today));
      row1.appendChild(pieSection);

      if (result.prediction) {
        const predSection = el('div', { class: 'usage-section prediction-section' });
        predSection.appendChild(createDailyLimitPrediction(result.prediction));
        row1.appendChild(predSection);
      }

      grid.appendChild(row1);
    }

    // Row 2: Weekly usage
    if (result.week) {
      const row2 = el('div', { class: 'usage-row' });
      const weekSection = el('div', { class: 'usage-section full-width' });
      weekSection.appendChild(createWeeklyUsageBarChart(result.week));
      row2.appendChild(weekSection);
      grid.appendChild(row2);
    }

    // Row 3: Block events + Trends
    if (result.blockStats && result.trends) {
      const row3 = el('div', { class: 'usage-row' });

      const blockSection = el('div', { class: 'usage-section block-section' });
      blockSection.appendChild(createBlockEventsSummary(result.blockStats));
      row3.appendChild(blockSection);

      const trendsSection = el('div', { class: 'usage-section trends-section' });
      trendsSection.appendChild(createUsageTrendsView(result.trends));
      row3.appendChild(trendsSection);

      grid.appendChild(row3);
    }

    content.appendChild(grid);
    container.appendChild(content);
  } catch (err) {
    loading.remove();
    const errorDiv = el('div', { class: 'usage-error' }, `Error loading analytics: ${(err as Error).message}`);
    container.appendChild(errorDiv);
    props.onError?.((err as Error).message);
  }

  return container;
}

// Helper: Inject styles
function injectUsageDashboardStyles() {
  if (document.getElementById('usage-dashboard-styles')) return;

  const style = el('style');
  style.id = 'usage-dashboard-styles';
  style.textContent = USAGE_DASHBOARD_STYLES;
  document.head.appendChild(style);
}
